import { TrafficCone, TrainFront, Bus, Fuel, Store, Pill, ClipboardList, Zap, SquareParking, Recycle, ShoppingCart, Waves } from 'lucide-react';
import { ink } from '../../lib/utils';

export function QuickActions({ navigate }) {
  // Eight, down from twenty-three.
  //
  // The twenty-three reproduced 77% of a 30-item menu that is already in the header above this
  // page and in the app's Servicios tab — so it was not a shortcut, it was a second menu, and it
  // cost 442px on desktop and 574px on a phone. A launcher earns its space by being shorter than
  // the menu: at eight you recognise, at twenty-three you read.
  //
  // What earns a slot: the page does not already show it, or it shows it only to people who
  // granted location. Everything inside Tu barrio — bus, farmacia, supermercado, bici, ERA,
  // contenedor — sits behind `userLocation &&`, and this app deliberately does not ask for GPS at
  // launch, so for anyone who declines, that whole block is a prompt and those services vanish
  // from the page entirely. Farmacias and Torrebus stay for them: a farmacia de guardia at 3am is
  // not something to go hunting through a menu for.
  //
  // What went, and where it already is: Tiempo (the hero card), Mapa (a nav item and an app tab),
  // Incidencias and Vecinos (Tu ciudad, plus a tab), Agenda and Boletines (Hoy en Torrelavega),
  // Contratos and Radiografía (Transparencia, where Contratos is now the headline), Residuos,
  // Supers, Torrebici and Parking (Tu barrio), Playas (a hero link), ZBE and La Patrona (two taps
  // in the menu, neither urgent). Descargas went to the menu's utilities: it is the "get the app"
  // page, this grid was its only route in, and it does not belong among city services.
  //
  // Precio Luz is new here — a daily household check that was in the menu and not in the launcher.
  //
  // Twelve at four columns, so it is three even rows at every width. Eight would have left a
  // ragged half-row on a phone and a single row of six plus two on desktop; the four that come
  // back are the ones with the same claim as Farmacias and Torrebus — Residuos, Supermercados
  // and Aparcamiento are all inside Tu barrio, which renders nothing at all without location
  // permission, and Playas has only a small link in the hero.
  const actions = [
    { Icon: Pill, label: 'Farmacias', color: '#10B981', path: '/farmacias' },
    { Icon: Bus, label: 'Torrebus', color: '#2D9F5B', path: '/torrebus' },
    { Icon: TrainFront, label: 'Cercanías', color: '#D83A3A', path: '/renfe' },
    { Icon: TrafficCone, label: 'Tráfico', color: '#EF4444', path: '/trafico' },
    { Icon: SquareParking, label: 'Aparcamiento', color: '#3B82F6', path: '/aparcamiento' },
    { Icon: Fuel, label: 'Gasolineras', color: '#F59E0B', path: '/gasolineras' },
    { Icon: Zap, label: 'Precio Luz', color: '#F59E0B', path: '/precio-luz' },
    { Icon: Recycle, label: 'Residuos', color: '#22C55E', path: '/residuos' },
    { Icon: ShoppingCart, label: 'Supermercados', color: '#F59E0B', path: '/supermercados' },
    { Icon: Store, label: 'Comercio', color: '#F59E0B', path: '/comercio' },
    { Icon: Waves, label: 'Playas', color: '#06B6D4', path: '/playas' },
    { Icon: ClipboardList, label: 'Trámites', color: '#6366F1', path: '/tramites' },
  ];

  return (
    <div className="mb-6 animate-fade-in-up">
      <div className="grid grid-cols-4 gap-2 sm:gap-2.5" data-testid="quick-actions-grid">
        {actions.map((action) => (
          <button key={action.label} onClick={() => navigate(action.path)} data-testid={`quick-action-${action.label.toLowerCase()}`}
            className="flex flex-col items-center gap-1.5 sm:gap-2 py-2.5 sm:py-3 rounded-xl hover:bg-slate-50 transition-colors active:scale-95 group">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110 border"
                 style={{ backgroundColor: `${action.color}1F`, borderColor: `${action.color}33` }}>
              <action.Icon size={24} strokeWidth={2} style={{ color: ink(action.color) }} aria-hidden="true" />
            </div>
            <p className="text-[11px] sm:text-[11px] font-bold text-slate-600 leading-tight text-center">{action.label}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
